import {forwardRef, useEffect, useImperativeHandle, useState} from 'react'
import {useParams} from 'react-router-dom'
import {promptAPI} from '../../../../services/PromptService'
import {PromptTemplate, PromptTemplateSpecField} from '../../core/_models'
import {FilterType, Option} from '../../../types/models'
import Select, {SingleValue, MultiValue} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import {Button} from 'react-bootstrap'
import {dataTableConfig} from '../../config/data-table'
import {updateQuery, resetLlmRules} from '../../slices/cdrFilterSlice'
import {useAppDispatch, useAppSelector} from '../../../../hooks/redux'
import {shallowEqual} from 'react-redux'

const genId = () => Math.random().toString(36).slice(2)

type AiFilterRule = {
  id: string
  fieldKey: string
  label: string
  filterType: FilterType
  operator: string
  value: any
  filterOptions?: PromptTemplateSpecField['filter_options']
}

const filterTypeMap: Record<string, FilterType> = {
  select: 'string',
  text: 'string',
  range: 'number',
  boolean: 'boolean',
  date: 'string',
}

const getDefaultValue = (type: FilterType) => {
  switch (type) {
    case 'multi-select':
      return []
    case 'boolean':
      return true
    case 'number':
      return ''
    default:
      return ''
  }
}

const isMultiValueOperator = (op: string) => op === 'inArray' || op === 'notInArray'

export type AiFiltersHandle = {
  apply: () => void
  reset: () => void
  count: () => number
  canApply: () => boolean
}

export const AiFilters = forwardRef<AiFiltersHandle, {onSelectionChange?: (ready: boolean) => void}>(function AiFilters(
  {onSelectionChange},
  ref
) {
  const {appId} = useParams()
  const dispatch = useAppDispatch()
  const query = useAppSelector((state) => state.cdrFilter.query, shallowEqual)
  const {data: prompts} = promptAPI.useGetPromptsQuery(appId!)
  const [selectedPrompt, setSelectedPrompt] = useState<PromptTemplate | null>(null)
  const [filters, setFilters] = useState<AiFilterRule[]>([])
  const [selectedField, setSelectedField] = useState<SingleValue<Option<string>>>(null)

  const filterableFields = (selectedPrompt?.spec?.fields ?? []).filter((f) => f.filterable)
  const availableFields = filterableFields.filter((f) => !filters.some((f2) => f2.fieldKey === f.key))

  useEffect(() => {
    onSelectionChange?.(filters.length > 0 || !!selectedPrompt)
  }, [filters.length, selectedPrompt, onSelectionChange])

  useImperativeHandle(ref, () => ({
    apply: handleApply,
    reset: removeAllFilters,
    count: () => filters.length,
    canApply: () => filters.length > 0 || !!selectedPrompt,
  }))

  useEffect(() => {
    if (!query?.rules_llm?.prompt_id || !prompts?.length) return

    const prompt = prompts.find((p) => p.id === query.rules_llm!.prompt_id)
    if (!prompt) return

    setSelectedPrompt(prompt)

    const fields = (prompt.spec?.fields ?? []).filter((f) => f.filterable)

    const restored = query
      .rules_llm!.items.map((item) => {
        const field = fields.find((f) => f.key === item.fieldKey)
        if (!field) return null

        const mappedType = filterTypeMap[field.filter_type ?? 'text'] ?? 'string'

        return {
          id: genId(),
          fieldKey: field.key!,
          label: field.label ?? field.key!,
          filterType: mappedType,
          operator: item.operator,
          value: item.value,
          filterOptions: field.filter_options,
        } as AiFilterRule
      })
      .filter(Boolean) as AiFilterRule[]

    setFilters(restored)
  }, [query?.rules_llm, prompts])

  const handlePromptChange = (option: SingleValue<Option<PromptTemplate>>) => {
    if (!option) {
      setSelectedPrompt(null)
      setFilters([])
      return
    }
    setSelectedPrompt(option.value)
    setFilters([])
  }

  const addFilter = (fieldKey: string) => {
    const field = filterableFields.find((f) => f.key === fieldKey)
    if (!field) return

    const mappedType = filterTypeMap[field.filter_type ?? 'text'] ?? 'string'
    const ops = dataTableConfig[mappedType]
    if (!ops || ops.length === 0) return

    setFilters((p) => [
      ...p,
      {
        id: genId(),
        fieldKey: field.key!,
        label: field.label ?? field.key!,
        filterType: mappedType,
        operator: ops[0].value,
        value: getDefaultValue(mappedType),
        filterOptions: field.filter_options,
      },
    ])
  }

  const updateFilter = (id: string, patch: Partial<AiFilterRule>) => {
    setFilters((p) => p.map((f) => (f.id === id ? {...f, ...patch} : f)))
  }

  const removeFilter = (id: string) => {
    setFilters((p) => p.filter((f) => f.id !== id))
  }

  const removeAllFilters = () => {
    setSelectedPrompt(null)
    setFilters([])
    setSelectedField(null)
    dispatch(resetLlmRules())
  }

  const handleApply = () => {
    dispatch(
      updateQuery({
        ...query,
        offset: 0,
        rules_llm: {
          operator: '$and',
          prompt_id: selectedPrompt?.id,
          items: filters.map(({fieldKey, operator, value}) => ({fieldKey, operator, value})),
        },
      })
    )
  }

  const renderValueInput = (f: AiFilterRule) => {
    const hasFilterOptions = Array.isArray(f.filterOptions) && f.filterOptions.length > 0

    if (f.filterType === 'string' && hasFilterOptions) {
      const options = (f.filterOptions as string[]).map((o) => ({label: o, value: o}))

      if (isMultiValueOperator(f.operator)) {
        const multiValue = Array.isArray(f.value) ? f.value : []
        return (
          <div onMouseDown={(e) => e.stopPropagation()}>
            <CreatableSelect<Option<string>, true>
              key={f.id}
              isMulti
              className='react-select-multi-custom react-select-solid'
              classNamePrefix='react-select'
              options={options}
              placeholder='Выберите или введите'
              value={multiValue.map((v: string) => ({label: v, value: v}))}
              onChange={(v: MultiValue<Option<string>>) => updateFilter(f.id, {value: v.map((o) => o.value)})}
            />
          </div>
        )
      }

      return (
        <div onMouseDown={(e) => e.stopPropagation()}>
          <Select
            className='react-select-styled react-select-solid'
            classNamePrefix='react-select'
            placeholder='Выберите значение'
            options={options}
            value={f.value ? {label: f.value, value: f.value} : null}
            onChange={(v: SingleValue<Option<string>>) => updateFilter(f.id, {value: v?.value ?? ''})}
          />
        </div>
      )
    }

    if (f.filterType === 'string' && isMultiValueOperator(f.operator)) {
      const multiValue = Array.isArray(f.value) ? f.value : []
      return (
        <CreatableSelect<Option<string>, true>
          key={f.id}
          isMulti
          className='react-select-multi-custom react-select-solid'
          classNamePrefix='react-select'
          placeholder='Введите значение и нажмите Enter или Tab'
          value={multiValue.map((v: string) => ({label: v, value: v}))}
          onChange={(v: MultiValue<Option<string>>) => updateFilter(f.id, {value: v.map((o) => o.value)})}
        />
      )
    }

    if (f.filterType === 'string') {
      return (
        <input
          id={f.id}
          className='form-control form-control-solid'
          value={f.value as string}
          placeholder='Введите значение'
          onChange={(e) => updateFilter(f.id, {value: e.target.value})}
        />
      )
    }

    if (f.filterType === 'number') {
      const opts = typeof f.filterOptions === 'object' && !Array.isArray(f.filterOptions) ? f.filterOptions : {}

      if (f.operator === 'isBetween') {
        return (
          <div className='d-flex gap-2 align-items-center'>
            <input
              type='number'
              className='form-control form-control-solid'
              placeholder={`от (число ${opts.min} - ${opts.max})`}
              min={opts.min}
              max={opts.max}
              step={opts.step}
              value={(f.value as any)?.from ?? ''}
              onChange={(e) =>
                updateFilter(f.id, {
                  value: {...(f.value as any), from: Number(e.target.value)},
                })
              }
            />
            <span className='text-muted'>—</span>
            <input
              type='number'
              className='form-control form-control-solid'
              placeholder={`до (число ${opts.min} - ${opts.max})`}
              min={opts.min}
              max={opts.max}
              step={opts.step}
              value={(f.value as any)?.to ?? ''}
              onChange={(e) =>
                updateFilter(f.id, {
                  value: {...(f.value as any), to: Number(e.target.value)},
                })
              }
            />
          </div>
        )
      }

      return (
        <input
          id={f.id}
          type='number'
          min={opts.min}
          max={opts.max}
          step={opts.step}
          placeholder={`Введите значение (число ${opts.min} - ${opts.max})`}
          className='form-control form-control-solid'
          value={(f.value as number) ?? ''}
          onChange={(e) => updateFilter(f.id, {value: Number(e.target.value)})}
        />
      )
    }

    if (f.filterType === 'boolean') {
      const boolValue = typeof f.value === 'boolean' ? f.value : true
      return (
        <div onMouseDown={(e) => e.stopPropagation()}>
          <Select<Option<boolean>>
            className='react-select-styled react-select-solid'
            classNamePrefix='react-select'
            options={[
              {label: 'Да', value: true},
              {label: 'Нет', value: false},
            ]}
            value={{label: boolValue ? 'Да' : 'Нет', value: boolValue}}
            onChange={(v: SingleValue<Option<boolean>>) => v && updateFilter(f.id, {value: v.value})}
          />
        </div>
      )
    }

    return (
      <input
        type='text'
        className='form-control form-control-solid'
        placeholder='Введите значение'
        value={(f.value as string) ?? ''}
        onChange={(e) => updateFilter(f.id, {value: e.target.value})}
      />
    )
  }

  return (
    <div className='d-flex flex-column'>
      <div>
        <div onMouseDown={(e) => e.stopPropagation()}>
          <Select
            className='react-select-styled react-select-solid py-2'
            classNamePrefix='react-select'
            placeholder='Выберите промт'
            options={(prompts ?? []).map((p) => ({
              label: p.name ?? 'Без имени',
              value: p,
            }))}
            value={selectedPrompt ? {label: selectedPrompt.name ?? 'Без имени', value: selectedPrompt} : null}
            onChange={handlePromptChange}
          />
        </div>
      </div>

      {filters.map((f, i) => {
        const ops = dataTableConfig[f.filterType].filter((o) => o.value !== 'startsWith' && o.value !== 'endsWith')

        return (
          <div key={f.id} className='align-items-center mb-2' style={{display: 'flex', gap: 6}}>
            {i === 0 ? (
              <span className='badge badge-light-primary min-w-50px justify-content-center'>где</span>
            ) : (
              <span className='min-w-50px' />
            )}
            <Select
              className='react-select-styled react-select-solid min-w-200px'
              classNamePrefix='react-select'
              options={[]}
              menuIsOpen={false}
              value={{label: f.label, value: f.fieldKey}}
              isDisabled={true}
              styles={{
                indicatorsContainer: () => ({display: 'none'}),
                indicatorSeparator: () => ({display: 'none'}),
              }}
            />
            <div onMouseDown={(e) => e.stopPropagation()}>
              <Select
                className='react-select-styled react-select-solid min-w-150px'
                classNamePrefix='react-select'
                options={ops}
                value={ops.find((o) => o.value === f.operator)}
                onChange={(v: SingleValue<Option<string>>) => v && updateFilter(f.id, {operator: v.value})}
              />
            </div>
            <div style={{flex: 1}}>{renderValueInput(f)}</div>
            <Button size='sm' variant='outline-danger' onClick={() => removeFilter(f.id)}>
              ✕
            </Button>
          </div>
        )
      })}

      {availableFields.length > 0 && (
        <div onMouseDown={(e) => e.stopPropagation()}>
          <Select
            className='react-select-styled react-select-solid py-2'
            classNamePrefix='react-select'
            placeholder='Добавить фильтр'
            value={selectedField}
            options={availableFields.map((f) => ({
              label: f.label ?? f.key ?? '',
              value: f.key ?? '',
            }))}
            onChange={(v: SingleValue<Option<string>>) => {
              if (!v) return
              addFilter(v.value)
              setSelectedField(null)
            }}
          />
        </div>
      )}
    </div>
  )
})
